/**
 * Maps SheetsData's per-MPN extraction-status strings into the
 * 5-state vocabulary the BOM tab UI cares about. Shared between the
 * BOM table column and the detail-panel header so both views agree
 * on what each state means.
 *
 * SheetsData reports one of:
 *   ready | extracting | pending | failed | not_extracted | no_source | unsupported
 *
 * (`no_source` and `unsupported` are real states observed in MCP
 * responses — SheetsData uses them when the part has no public PDF
 * source it can find, or when the part class isn't supported.)
 *
 * Backend additionally re-emits cached terminal states from the
 * `app.datasheet_status` table — `ready` (cached extraction success)
 * or `failed` (cached "permanently unavailable").
 *
 * We collapse:
 *   ready                            → "ready"      (overview fetchable)
 *   extracting | pending             → "extracting" (work in flight)
 *   failed | no_source | unsupported → "failed"     (terminal, no auto-retry)
 *   not_extracted                    → "missing"    (transient, never queued)
 *   ""                               → "missing"    (no info yet)
 *
 * Recently-queued override
 * ------------------------
 * The bulk-status poll fires every 5 s while at least one MPN is in
 * flight. SheetsData has a propagation lag — `prefetch_datasheets`
 * returns `{status: "queued"}` for an MPN, but `check_extraction_status`
 * called 80 ms later still says `not_extracted` for that same MPN
 * (different cache layer on their side, not yet synchronised). With
 * the bare mapping above the user's Extract click would visibly
 * bounce: hourglass → Extract button → next 5 s tick → hourglass...
 *
 * Fix: track which MPNs were queued in this browser session via
 * `markRecentlyQueued`. For roughly 90 s after a queue, treat
 * `not_extracted` for that MPN as `extracting` so the row stays put
 * until SheetsData transitions to a real state (extracting / pending
 * / ready / failed / no_source / unsupported), all of which override
 * the local hint. After 90 s the override expires; if SheetsData
 * still says `not_extracted` we assume the queue silently dropped and
 * surface the Extract affordance again.
 */

export type DatasheetStatus =
  | "ready"
  | "extracting"
  | "missing"
  | "failed"
  | "no_mpn";

// `parts` is loosely typed as `Record<string, unknown>` upstream because
// the bulk endpoint returns whatever SheetsData echoed back. We narrow
// to a string here before pattern-matching.
export interface ExtractionStatusPayload {
  parts?: Record<string, unknown> | null;
}

// Module-level recently-queued tracker. MPN → epoch-ms timestamp of
// the most recent Extract click. `useExtractDatasheet.onMutate`
// writes here; `datasheetStatusFor` reads here and uses the entry to
// override `not_extracted` (which would otherwise bounce the row
// back to the Extract button) into `extracting` until either:
//   - SheetsData reports a real state (handled before the override
//     check runs), or
//   - the entry ages past `RECENTLY_QUEUED_TTL_MS`.
//
// Plain Map at module scope rather than a Zustand store / React Ref
// because there's no UI that needs to subscribe to changes — React
// re-renders are driven by the React Query cache update, and this
// map is consulted lazily during render. Lives for the browser tab's
// lifetime; nothing to clean up.
const recentlyQueued = new Map<string, number>();
const RECENTLY_QUEUED_TTL_MS = 90_000;

export function markRecentlyQueued(mpn: string): void {
  if (!mpn) return;
  recentlyQueued.set(mpn, Date.now());
}

export function datasheetStatusFor(
  mpn: string | undefined | null,
  extraction: ExtractionStatusPayload | undefined | null,
): DatasheetStatus {
  if (!mpn) return "no_mpn";
  const parts = extraction?.parts;
  if (!parts) return "missing";
  const raw = parts[mpn];
  if (typeof raw !== "string" || !raw) return "missing";

  if (raw === "ready") return "ready";
  if (raw === "extracting" || raw === "pending") return "extracting";
  // `no_source` and `unsupported` are SheetsData's softer "we tried,
  // can't do it" states — terminal in practice, treat them as failed
  // alongside the explicit `failed`.
  if (raw === "failed" || raw === "no_source" || raw === "unsupported") {
    return "failed";
  }

  // `not_extracted` and unrecognised states. If we recently fired a
  // queue request for this MPN, mask the propagation lag: SheetsData's
  // `check_extraction_status` may return `not_extracted` for ~10–20 s
  // after `prefetch_datasheets` returns `{status: "queued"}`. Without
  // this override the user sees their Extract click flicker back to
  // the button on the next polling tick.
  const queuedAt = recentlyQueued.get(mpn);
  if (queuedAt !== undefined) {
    if (Date.now() - queuedAt < RECENTLY_QUEUED_TTL_MS) {
      return "extracting";
    }
    // TTL expired — drop the entry so the map doesn't grow without
    // bound across a long session.
    recentlyQueued.delete(mpn);
  }

  return "missing";
}
